import React, { useMemo } from 'react';
import { Text, View, Image } from '@react-pdf/renderer';
import { TFunction } from 'i18next';
import { unit as unitEnum } from '../../../../../enum/project';
import { convertFieldValue } from '../../../../../services/UnitConversionService';
import { pdfSharedStyles } from '../../../pdfSharedStyles';
import { ensureFontsRegistered } from '../../../shared/PdfFontRegistry';
import * as symbols from '../../../../../enum/symbols';

// Funzione di parsing base per <br> e <strong>
import { ValueUnitCells } from '../../shared/pdfUnitMerge';

function parseHtmlToPdfText(html: string, style?: any) {
    // Sostituisce <br> con \n
    const withBreaks = html.replace(/<br\s*\/??\s*>/gi, '\n');
    // Regex per trovare <strong>...</strong>
    const regex = /(<strong>(.*?)<\/strong>)/gi;
    const result: JSX.Element[] = [];
    let lastIndex = 0;
    let match;
    let key = 0;

    while ((match = regex.exec(withBreaks)) !== null) {
        // Testo prima del tag
        if (match.index > lastIndex) {
            const text = withBreaks.substring(lastIndex, match.index);
            if (text) result.push(<Text key={key++} style={style}>{text}</Text>);
        }
        // Testo in grassetto
        const boldText = match[2];
        if (boldText) {
            result.push(
                <Text
                    key={key++}
                    style={{
                        ...style,
                        fontFamily: 'DejaVuSans',
                        fontWeight: 'bold',
                    }}
                >
                    {boldText}
                </Text>
            );
        }
        lastIndex = regex.lastIndex;
    }
    // Testo dopo l'ultimo tag
    if (lastIndex < withBreaks.length) {
        const text = withBreaks.substring(lastIndex);
        if (text) result.push(<Text key={key++} style={style}>{text}</Text>);
    }
    return result;
}

interface AirportResultsCoreProps {
    images: {
        tireImageUnstabilized?: string;
        tireImageIncreasedPassages?: string;
        tireImageGeogrid?: string;
    };
    projectData: any;
    output?: any;
    aashtoResults?: any;
    t: TFunction;
    showInputData?: boolean;
    showResults?: boolean;
    customTitle?: string;
    // Dynamic design sections - optional for backward compatibility
    surfaceDesignImages?: {
        canvas1?: string;
        canvas2?: string;
        canvas3?: string;
        tableData1?: { passesFirstYear?: number; w18?: number; thickness?: number };
        tableData2?: { passesFirstYear?: number; newW18?: number; tbr?: number };
        tableData3?: { passesFirstYear?: number; w18?: number; thicknessSaving?: number };
    };
    baseDesignImages?: {
        canvas1?: string;
        canvas2?: string;
        canvas3?: string;
        tableData1?: { passesFirstYear?: number; w18?: number; thickness?: number };
        tableData2?: { passesFirstYear?: number; newW18?: number; tbr?: number };
        tableData3?: { passesFirstYear?: number; w18?: number; thicknessSaving?: number };
    };
    subbaseDesignImages?: {
        canvas1?: string;
        canvas2?: string;
        canvas3?: string;
        tableData1?: { passesFirstYear?: number; w18?: number; thickness?: number };
        tableData2?: { passesFirstYear?: number; newW18?: number; tbr?: number };
        tableData3?: { passesFirstYear?: number; w18?: number; thicknessSaving?: number };
    };
}

export const AirportResultsCore: React.FC<AirportResultsCoreProps> = ({
    images,
    projectData,
    output,
    aashtoResults: propAashtoResults,
    t,
    showInputData = true,
    showResults = true,
    customTitle,
    surfaceDesignImages,
    baseDesignImages,
    subbaseDesignImages,
}) => {
    ensureFontsRegistered();
    const unitSystem = projectData?.unit || 'Metric';

    // Helper for consistent conversion and rounding for input tables
    const convertInputValue = (value: number | undefined, fieldName: string, metricUnit: string, imperialUnit: string): string => {
        if (typeof value !== 'number') return 'N/A';
        if (unitSystem === 'Imperial') {
            const converted = convertFieldValue({ name: fieldName, type: 'number', unit: [metricUnit, imperialUnit], isConvertible: true }, value, unitEnum.SI, unitEnum.Imperial, `pdf.${fieldName}`);
            return converted !== null ? converted.toFixed(3) : value.toString();
        }
        return value.toString();
    };

    // Helper function to render traffic analysis table
    const renderTrafficTable = (title: string, data: any, backgroundColor = '#f9f9f9') => (
        <View style={{ width: '100%', marginTop: 8, padding: 8, border: '1 solid #ddd', borderRadius: 4, backgroundColor }}>
            <Text style={{ fontSize: 10, fontWeight: 'bold', textAlign: 'left', marginBottom: 6 }}>{title}</Text>
            <View style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {data?.passesFirstYear !== undefined && (
                    <View style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', gap: 1 }}>
                        <Text style={{ fontSize: 8 }}>{t('passes_first_year', 'Nr. of passes in 1st year')}:</Text>
                        <Text style={{ fontSize: 8, fontWeight: 'bold' }}>{data.passesFirstYear.toLocaleString('en-US', { maximumFractionDigits: 0 })}</Text>
                    </View>
                )}
                {data?.w18 !== undefined && (
                    <View style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', gap: 1 }}>
                        <Text style={{ fontSize: 8 }}>{t('w18', 'W18')}:</Text>
                        <Text style={{ fontSize: 8, fontWeight: 'bold' }}>{data.w18.toFixed(3)}</Text>
                    </View>
                )}
                {data?.newW18 !== undefined && (
                    <View style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', gap: 1 }}>
                        <Text style={{ fontSize: 8 }}>{t('new_w18', 'New W18')}:</Text>
                        <Text style={{ fontSize: 8, fontWeight: 'bold' }}>{data.newW18.toFixed(3)}</Text>
                    </View>
                )}
                {data?.totalEsal !== undefined && (
                    <View style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', gap: 1 }}>
                        <Text style={{ fontSize: 8 }}>{t('total_esal', 'Total ESAL')}:</Text>
                        <Text style={{ fontSize: 8, fontWeight: 'bold' }}>{data.totalEsal.toExponential(2)}</Text>
                    </View>
                )}
                {data?.tbr !== undefined && (
                    <View style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', gap: 1 }}>
                        <Text style={{ fontSize: 8 }}>{t('tbr', 'TBR')}:</Text>
                        <Text style={{ fontSize: 8, fontWeight: 'bold' }}>{data.tbr.toFixed(1)}</Text>
                    </View>
                )}
                {data?.thicknessSaving !== undefined && (
                    <View style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', gap: 1 }}>
                        <Text style={{ fontSize: 8 }}>{t('thickness_saving', 'Thickness Saving')}:</Text>
                        <Text style={{ fontSize: 8, fontWeight: 'bold' }}>{data.thicknessSaving.toFixed(0)}%</Text>
                    </View>
                )}
            </View>
        </View>
    );

    // Extract project inputs
    const projectInputs = projectData?.projectInfo?.data;

    // Extract AASHTO results
    const aashtoResults = propAashtoResults || projectData?.aashtoResults;

    // Extract real data from AASHTO results - use correct data paths matching usePdfExport pattern
    const esalFactors = aashtoResults?.detailedCalculations?.esalFactors;
    const inputEsal = projectInputs?.trafficLoad?.esalFinalValue;

    // Calculate thickness saving from comparing unstabilised vs stabilised
    const softwareDesign = aashtoResults?.detailedCalculations?.softwareDesign;
    const unstabilisedTotal = softwareDesign
        ? (softwareDesign.surface?.unstabilised || 0) + (softwareDesign.base?.unstabilised || 0) + (softwareDesign.subbase?.unstabilised || 0)
        : 0;
    const stabilisedTotal = softwareDesign
        ? (softwareDesign.surface?.stabilised || 0) + (softwareDesign.base?.stabilised || 0) + (softwareDesign.subbase?.stabilised || 0)
        : 0;
    const thicknessSaving = unstabilisedTotal > 0 ? ((unstabilisedTotal - stabilisedTotal) / unstabilisedTotal) * 100 : 0;

    const trafficData = aashtoResults
        ? {
              unstabilized: {
                  totalEsal: inputEsal || esalFactors?.esalUnstabilised || 0,
              },
              increasedPassages: {
                  tbr: esalFactors?.tbr || 0,
              },
              optimized: {
                  thicknessSaving: thicknessSaving > 0 ? thicknessSaving : 0,
              },
          }
        : null;

    // === SEPARATE INPUT TABLES FOR EACH FORM GROUP (following Unpaved/Railway pattern) ===

    // 1. Road Information table
    const roadInfoInputs = useMemo(() => {
        const inputs = [];
        // FLAT structure: data is directly on projectInfo.data
        const methodData = projectData?.projectInfo?.data as any;

        if (!methodData) return [];

        const getUnit = (metricUnit: string, imperialUnit: string) => {
            return unitSystem === 'Metric' ? metricUnit : imperialUnit;
        };

        if (methodData.roadInfo) {
            const roadInfoData = methodData.roadInfo;
            if (roadInfoData.carriagewayWidth !== undefined) {
                inputs.push([
                    t('width_of_carriageway_one_direction', 'Carriageway Width'),
                    'W',
                    convertInputValue(roadInfoData.carriagewayWidth, 'carriagewayWidth', 'm', 'ft'),
                    getUnit('m', 'ft'),
                ]);
            }
            if (roadInfoData.longitudinalSlope !== undefined) {
                inputs.push([t('longitudinal_slope', 'Longitudinal Slope'), 'S', roadInfoData.longitudinalSlope.toString(), '%']);
            }
            if (roadInfoData.transversalSlope !== undefined) {
                inputs.push([t('transversal_slope', 'Transversal Slope'), symbols.sym_sx, roadInfoData.transversalSlope.toString(), '%']);
            }
        }

        return inputs;
    }, [projectData, t, convertInputValue, unitSystem]);

    // 2. Traffic Load table
    const trafficLoadInputs = useMemo(() => {
        const inputs = [];
        // FLAT structure: data is directly on projectInfo.data
        const methodData = projectData?.projectInfo?.data as any;

        if (!methodData) return [];

        if (methodData.trafficLoad) {
            const trafficLoadData = methodData.trafficLoad;
            if (trafficLoadData.functionalClassification) {
                inputs.push([t('functional_classification', 'Functional Classification'), '', trafficLoadData.functionalClassification, '']);
            }
            if (trafficLoadData.roadType) {
                inputs.push([t('road_type', 'Road Type'), '', trafficLoadData.roadType, '']);
            }
            if (trafficLoadData.reliabilityLevel !== undefined) {
                inputs.push([t('reliability_level_r', 'Reliability Level'), 'R', trafficLoadData.reliabilityLevel.toString(), '%']);
            }
            if (trafficLoadData.combinedStandardError !== undefined) {
                inputs.push([t('combined_standard_error_so', 'Combined Standard Error'), symbols.sym_so, trafficLoadData.combinedStandardError.toString(), '']);
            }
            if (trafficLoadData.initialServiceIndex !== undefined) {
                inputs.push([t('initial_service_index_po', 'Initial Service Index'), symbols.sym_po, trafficLoadData.initialServiceIndex.toString(), '']);
            }
            if (trafficLoadData.terminalSurfaceIndex !== undefined) {
                inputs.push([t('terminal_surface_index_pt', 'Terminal Surface Index'), symbols.sym_pt, trafficLoadData.terminalSurfaceIndex.toString(), '']);
            }
            if (trafficLoadData.esalFinalValue !== undefined) {
                inputs.push([t('esal_final_value', 'ESAL Final Value'), 'ESAL', trafficLoadData.esalFinalValue.toString(), '']);
            }
        }

        return inputs;
    }, [projectData, t]);

    // 3. Surface table
    const surfaceInputs = useMemo(() => {
        const inputs = [];
        // FLAT structure: data is directly on projectInfo.data
        const methodData = projectData?.projectInfo?.data as any;

        if (!methodData) return [];

        const getUnit = (metricUnit: string, imperialUnit: string) => {
            return unitSystem === 'Metric' ? metricUnit : imperialUnit;
        };

        if (methodData.surface) {
            const surfaceData = methodData.surface;
            if (surfaceData.surfaceMaterial) {
                inputs.push([t('select_surface_material', 'Surface Material'), '', t(surfaceData.surfaceMaterial), '']);
            }
            if (surfaceData.minThickness !== undefined) {
                inputs.push([t('minimum_thickness_of_surface_layer', 'Min Surface Thickness'), symbols.sym_d1, convertInputValue(surfaceData.minThickness, 'minThickness', 'm', 'ft'), getUnit('m', 'ft')]);
            }
        }

        return inputs;
    }, [projectData, t, convertInputValue, unitSystem]);

    // 4. Base Course table
    const baseCourseInputs = useMemo(() => {
        const inputs = [];
        // FLAT structure: data is directly on projectInfo.data
        const methodData = projectData?.projectInfo?.data as any;

        if (!methodData) return [];

        const getUnit = (metricUnit: string, imperialUnit: string) => {
            return unitSystem === 'Metric' ? metricUnit : imperialUnit;
        };

        if (methodData.baseCourse) {
            const baseCourseData = methodData.baseCourse;
            if (baseCourseData.baseMaterial) {
                inputs.push([t('select_base_material', 'Base Material'), '', t(baseCourseData.baseMaterial), '']);
            }
            if (baseCourseData.minThickness !== undefined) {
                inputs.push([t('minimum_thickness_of_base_layer', 'Min Base Thickness'), symbols.sym_d2, convertInputValue(baseCourseData.minThickness, 'minThickness', 'm', 'ft'), getUnit('m', 'ft')]);
            }
            if (baseCourseData.baseGeogrid && baseCourseData.baseGeogrid.trim() !== '') {
                inputs.push([t('select_geogrid_for_base_course', 'Base Geogrid'), '', baseCourseData.baseGeogrid, '']);
            }
        }

        return inputs;
    }, [projectData, t, convertInputValue, unitSystem]);

    // 5. Subbase Course table
    const subbaseCourseInputs = useMemo(() => {
        const inputs = [];
        // FLAT structure: data is directly on projectInfo.data
        const methodData = projectData?.projectInfo?.data as any;

        if (!methodData) return [];

        const getUnit = (metricUnit: string, imperialUnit: string) => {
            return unitSystem === 'Metric' ? metricUnit : imperialUnit;
        };

        if (methodData.subbaseCourse) {
            const subbaseCourseData = methodData.subbaseCourse;
            if (subbaseCourseData.subbaseMaterial) {
                inputs.push([t('select_subbase_material', 'Subbase Material'), '', t(subbaseCourseData.subbaseMaterial), '']);
            }
            if (subbaseCourseData.minThickness !== undefined) {
                inputs.push([
                    t('minimum_thickness_of_subbase_layer', 'Min Subbase Thickness'),
                    symbols.sym_d3,
                    convertInputValue(subbaseCourseData.minThickness, 'minThickness', 'm', 'ft'),
                    getUnit('m', 'ft'),
                ]);
            }
            if (subbaseCourseData.subbaseGeogrid && subbaseCourseData.subbaseGeogrid.trim() !== '') {
                inputs.push([t('select_geogrid_for_subbase_course', 'Subbase Geogrid'), '', subbaseCourseData.subbaseGeogrid, '']);
            }
        }

        return inputs;
    }, [projectData, t, convertInputValue, unitSystem]);

    // 6. Subgrade table
    const subgradeInputs = useMemo(() => {
        const inputs = [];
        // FLAT structure: data is directly on projectInfo.data
        const methodData = projectData?.projectInfo?.data as any;

        if (!methodData) return [];

        const inputMethod = methodData.roadInfo?.subgradeInputMethod;
        if (methodData.subgrade && inputMethod) {
            const subgradeData = methodData.subgrade;
            if (inputMethod === 'cbr') {
                if (subgradeData.cbrSubgrade !== undefined) {
                    inputs.push([t('cbr_subgrade_label', 'CBR Subgrade'), 'CBR', subgradeData.cbrSubgrade.toString(), '%']);
                }
            } else if (inputMethod === 'ev2') {
                if (subgradeData.ev2Subgrade !== undefined) {
                    inputs.push([t('ev2_subgrade_label', 'Ev2 Subgrade'), symbols.sym_ev2, convertInputValue(subgradeData.ev2Subgrade, 'ev2Subgrade', 'MPa', 'psi'), unitSystem === 'Metric' ? 'MPa' : 'psi']);
                }
            }
        }

        return inputs;
    }, [projectData, t, convertInputValue, unitSystem]);

    // Check if AASHTO calculation results exist (for hasInputData condition)
    const airportCalculationResults = useMemo(() => {
        if (!aashtoResults) return null;
        // Just return a truthy object if results exist - actual data comes from trafficData above
        return { hasResults: true };
    }, [aashtoResults]);

    // Helper function to render input table (following Unpaved/Railway pattern)
    // Accepts arrays with string or React.ReactNode elements (for subscript symbols)
    const renderInputTable = (tableData: (string | React.ReactNode)[][], tableTitle: string, testId?: string) => {
        if (tableData.length === 0) return null;

        return (
            <View style={pdfSharedStyles.section}>
                <View style={pdfSharedStyles.spacer1} />
                <Text style={pdfSharedStyles.tableTitle}>{tableTitle}</Text>
                <View style={pdfSharedStyles.spacer0} />
                <View style={pdfSharedStyles.table}>
                    <View style={pdfSharedStyles.tableRow}>
                        <View style={[pdfSharedStyles.tableColHeader, pdfSharedStyles.dimFirstCol]}>
                            <Text style={[pdfSharedStyles.tableCellHeader, pdfSharedStyles.leftCell]}>{t('output_description')}</Text>
                        </View>
                        <View style={pdfSharedStyles.tableColHeaderSymbol}>
                            <Text style={pdfSharedStyles.tableCellHeader}>{t('output_symbol')}</Text>
                        </View>
                        <View style={pdfSharedStyles.tableColHeaderValue}>
                            <Text style={pdfSharedStyles.tableCellHeader}>{t('output_value')}</Text>
                        </View>
                        <View style={pdfSharedStyles.tableColHeaderUnit}>
                            <Text style={pdfSharedStyles.tableCellHeader}>{t('output_unit')}</Text>
                        </View>
                    </View>
                    {tableData.map((row, idx) => (
                        <View style={[pdfSharedStyles.tableRow, idx % 2 === 0 ? pdfSharedStyles.evenRow : pdfSharedStyles.oddRow]} key={`${testId || 'input'}-${idx}`}>
                            <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{row[0]}</Text>
                            </View>
                            <View style={pdfSharedStyles.tableColSymbol}>
                                <Text style={pdfSharedStyles.tableCell}>{row[1]}</Text>
                            </View>
                            <ValueUnitCells value={row[2]} unit={row[3]} />
                        </View>
                    ))}
                </View>
            </View>
        );
    };

    // Determine if there is input data to display
    const hasInputData =
        roadInfoInputs.length > 0 ||
        trafficLoadInputs.length > 0 ||
        surfaceInputs.length > 0 ||
        baseCourseInputs.length > 0 ||
        subbaseCourseInputs.length > 0 ||
        subgradeInputs.length > 0 ||
        airportCalculationResults;

    return (
        <>
            {/* Title Section */}
            {customTitle && (
                <>
                    <View style={pdfSharedStyles.spacer1} />
                    <Text style={pdfSharedStyles.title}>{customTitle}</Text>
                    <View style={pdfSharedStyles.spacer1} />
                </>
            )}

            {/* MACREAD Airport Description */}
            <View style={pdfSharedStyles.spacer1} />
            <Text style={[pdfSharedStyles.projectInfoProjectType]}>GROUNDMAC - Airport modified</Text>
            <View style={pdfSharedStyles.spacer0} />
            <Text>{parseHtmlToPdfText(t('airport_project_info_desc_pdf'), pdfSharedStyles.projectInfoSubtitle)}</Text>

            {/* Input Data Summary Section */}
            {showInputData && hasInputData && (
                <View style={pdfSharedStyles.section}>
                    <View style={pdfSharedStyles.spacer1} />
                    <Text style={[pdfSharedStyles.title, { marginTop: 8, marginBottom: 0, textAlign: 'left' }]}>{t('input_data_summary', 'Input Data Summary')}</Text>
                    <View style={pdfSharedStyles.spacer0} />

                    {/* Road Information table */}
                    {renderInputTable(roadInfoInputs, t('road_information', 'Road Information'), 'road-info')}

                    {/* Traffic Load table */}
                    {renderInputTable(trafficLoadInputs, t('traffic_load', 'Traffic Load'), 'traffic-load')}

                    {/* Page break after Traffic Load */}
                    <View break />

                    {/* Surface table */}
                    {renderInputTable(surfaceInputs, t('surface', 'Surface'), 'surface')}

                    {/* Base Course table */}
                    {renderInputTable(baseCourseInputs, t('base_course', 'Base Course'), 'base-course')}

                    {/* Subbase Course table */}
                    {renderInputTable(subbaseCourseInputs, t('subbase_course', 'Subbase Course'), 'subbase-course')}

                    {/* Subgrade table */}
                    {renderInputTable(subgradeInputs, t('subgrade', 'Subgrade'), 'subgrade')}
                </View>
            )}

            {/* Results Section - Canvas Images */}
            {showResults && (
                <>
                    <View style={pdfSharedStyles.spacer1} />
                    <Text style={[pdfSharedStyles.title, pdfSharedStyles.resultTitle]}>{t('results', 'Results')}</Text>
                    <View style={pdfSharedStyles.spacer1} />

                    {/* Airport Images - 3 Fixed Canvases */}
                    {(images.tireImageUnstabilized || images.tireImageIncreasedPassages) && (
                        <View style={pdfSharedStyles.graphicImagesContainer}>
                            <View style={{ alignItems: 'center', justifyContent: 'flex-end', width: '33.33%', height: '300' }}>
                                {images.tireImageUnstabilized ? (
                                    <Image key={images.tireImageUnstabilized.slice(0, 32)} style={pdfSharedStyles.graphicImage} src={images.tireImageUnstabilized} />
                                ) : (
                                    <Text style={pdfSharedStyles.graphicDescription}>Unstabilized image not available</Text>
                                )}
                                <Text style={pdfSharedStyles.graphicDescription}>{t('unstabilized', 'Unstabilized')}</Text>
                                {trafficData?.unstabilized && renderTrafficTable('Traffic Analysis', trafficData.unstabilized, '#f9f9f9')}
                            </View>
                            <View style={{ alignItems: 'center', justifyContent: 'flex-end', width: '33.33%', height: '300' }}>
                                {images.tireImageIncreasedPassages ? (
                                    <Image key={images.tireImageIncreasedPassages.slice(0, 32)} style={pdfSharedStyles.graphicImage} src={images.tireImageIncreasedPassages} />
                                ) : (
                                    <Text style={pdfSharedStyles.graphicDescription}>Increased passages image not available</Text>
                                )}
                                <Text style={pdfSharedStyles.graphicDescription}>{t('increased_passages', 'Increased Passages')}</Text>
                                {trafficData?.increasedPassages && renderTrafficTable('Traffic Benefit Analysis', trafficData.increasedPassages, '#e8f5e8')}
                            </View>
                            {/* tireImageGeogrid terza immagine */}
                            {images.tireImageGeogrid && (
                                <View style={{ alignItems: 'center', justifyContent: 'flex-end', width: '33.33%', height: '300' }}>
                                    <Image key={images.tireImageGeogrid.slice(0, 32)} style={pdfSharedStyles.graphicImage} src={images.tireImageGeogrid} />
                                    <Text style={pdfSharedStyles.graphicDescription}>{t('geogrid_stabilized', 'GEOGRID Stabilized')}</Text>
                                    {trafficData?.optimized && renderTrafficTable('Optimized Design', trafficData.optimized, '#fff3e0')}
                                </View>
                            )}
                        </View>
                    )}

                    {/* Dynamic Design Sections - Airport specific (3 Optional Canvas Groups) */}
                    {(surfaceDesignImages || baseDesignImages || subbaseDesignImages) && (
                        <View>
                            <View style={pdfSharedStyles.spacer1} />

                            {/* Surface Design Section */}
                            {surfaceDesignImages && (surfaceDesignImages.canvas1 || surfaceDesignImages.canvas2 || surfaceDesignImages.canvas3) && (
                                <View>
                                    <Text style={[pdfSharedStyles.title, { fontSize: 14, marginBottom: 8 }]}>{t('surface_stabilized_section', 'Surface Stabilized Section')}</Text>
                                    <View style={pdfSharedStyles.graphicImagesContainer}>
                                        {surfaceDesignImages.canvas1 && (
                                            <View style={{ alignItems: 'center', justifyContent: 'flex-end', width: '33.33%', height: '300' }}>
                                                <Image key={surfaceDesignImages.canvas1.slice(0, 32)} style={pdfSharedStyles.graphicImage} src={surfaceDesignImages.canvas1} />
                                                <Text style={pdfSharedStyles.graphicDescription}>{t('unstabilized', 'Unstabilized')}</Text>
                                                {surfaceDesignImages.tableData1 && renderTrafficTable('Traffic Analysis', surfaceDesignImages.tableData1, '#f9f9f9')}
                                            </View>
                                        )}
                                        {surfaceDesignImages.canvas2 && (
                                            <View style={{ alignItems: 'center', justifyContent: 'flex-end', width: '33.33%', height: '300' }}>
                                                <Image key={surfaceDesignImages.canvas2.slice(0, 32)} style={pdfSharedStyles.graphicImage} src={surfaceDesignImages.canvas2} />
                                                <Text style={pdfSharedStyles.graphicDescription}>{t('increased_passages', 'Increased Passages')}</Text>
                                                {surfaceDesignImages.tableData2 && renderTrafficTable('Traffic Benefit Analysis', surfaceDesignImages.tableData2, '#e8f5e8')}
                                            </View>
                                        )}
                                        {surfaceDesignImages.canvas3 && (
                                            <View style={{ alignItems: 'center', justifyContent: 'flex-end', width: '33.33%', height: '300' }}>
                                                <Image key={surfaceDesignImages.canvas3.slice(0, 32)} style={pdfSharedStyles.graphicImage} src={surfaceDesignImages.canvas3} />
                                                <Text style={pdfSharedStyles.graphicDescription}>{t('geogrid_stabilized', 'GEOGRID Stabilized')}</Text>
                                                {surfaceDesignImages.tableData3 && renderTrafficTable('Optimized Design', surfaceDesignImages.tableData3, '#fff3e0')}
                                            </View>
                                        )}
                                    </View>
                                    <View style={pdfSharedStyles.spacer1} />
                                </View>
                            )}

                            {/* Base Design Section */}
                            {baseDesignImages && (baseDesignImages.canvas1 || baseDesignImages.canvas2 || baseDesignImages.canvas3) && (
                                <>
                                    <View break />
                                    <View>
                                        <Text style={[pdfSharedStyles.title, { fontSize: 14, marginBottom: 8 }]}>{t('base_stabilized_section', 'Base Stabilized Section')}</Text>
                                        <View style={pdfSharedStyles.graphicImagesContainer}>
                                            {baseDesignImages.canvas1 && (
                                                <View style={{ alignItems: 'center', justifyContent: 'flex-end', width: '33.33%', height: '300' }}>
                                                    <Image key={baseDesignImages.canvas1.slice(0, 32)} style={pdfSharedStyles.graphicImage} src={baseDesignImages.canvas1} />
                                                    <Text style={pdfSharedStyles.graphicDescription}>{t('unstabilized', 'Unstabilized')}</Text>
                                                    {baseDesignImages.tableData1 && renderTrafficTable('Traffic Analysis', baseDesignImages.tableData1, '#f9f9f9')}
                                                </View>
                                            )}
                                            {baseDesignImages.canvas2 && (
                                                <View style={{ alignItems: 'center', justifyContent: 'flex-end', width: '33.33%', height: '300' }}>
                                                    <Image key={baseDesignImages.canvas2.slice(0, 32)} style={pdfSharedStyles.graphicImage} src={baseDesignImages.canvas2} />
                                                    <Text style={pdfSharedStyles.graphicDescription}>{t('increased_passages', 'Increased Passages')}</Text>
                                                    {baseDesignImages.tableData2 && renderTrafficTable('Traffic Benefit Analysis', baseDesignImages.tableData2, '#e8f5e8')}
                                                </View>
                                            )}
                                            {baseDesignImages.canvas3 && (
                                                <View style={{ alignItems: 'center', justifyContent: 'flex-end', width: '33.33%', height: '300' }}>
                                                    <Image key={baseDesignImages.canvas3.slice(0, 32)} style={pdfSharedStyles.graphicImage} src={baseDesignImages.canvas3} />
                                                    <Text style={pdfSharedStyles.graphicDescription}>{t('geogrid_stabilized', 'GEOGRID Stabilized')}</Text>
                                                    {baseDesignImages.tableData3 && renderTrafficTable('Optimized Design', baseDesignImages.tableData3, '#fff3e0')}
                                                </View>
                                            )}
                                        </View>
                                        <View style={pdfSharedStyles.spacer1} />
                                    </View>
                                </>
                            )}

                            {/* Subbase Design Section */}
                            {subbaseDesignImages && (subbaseDesignImages.canvas1 || subbaseDesignImages.canvas2 || subbaseDesignImages.canvas3) && (
                                <>
                                    <View break />
                                    <View>
                                        <Text style={[pdfSharedStyles.title, { fontSize: 14, marginBottom: 8 }]}>{t('subbase_stabilized_section', 'Subbase Stabilized Section')}</Text>
                                        <View style={pdfSharedStyles.graphicImagesContainer}>
                                            {subbaseDesignImages.canvas1 && (
                                                <View style={{ alignItems: 'center', justifyContent: 'flex-end', width: '33.33%', height: '300' }}>
                                                    <Image key={subbaseDesignImages.canvas1.slice(0, 32)} style={pdfSharedStyles.graphicImage} src={subbaseDesignImages.canvas1} />
                                                    <Text style={pdfSharedStyles.graphicDescription}>{t('unstabilized', 'Unstabilized')}</Text>
                                                    {subbaseDesignImages.tableData1 && renderTrafficTable('Traffic Analysis', subbaseDesignImages.tableData1, '#f9f9f9')}
                                                </View>
                                            )}
                                            {subbaseDesignImages.canvas2 && (
                                                <View style={{ alignItems: 'center', justifyContent: 'flex-end', width: '33.33%', height: '300' }}>
                                                    <Image key={subbaseDesignImages.canvas2.slice(0, 32)} style={pdfSharedStyles.graphicImage} src={subbaseDesignImages.canvas2} />
                                                    <Text style={pdfSharedStyles.graphicDescription}>{t('increased_passages', 'Increased Passages')}</Text>
                                                    {subbaseDesignImages.tableData2 && renderTrafficTable('Traffic Benefit Analysis', subbaseDesignImages.tableData2, '#e8f5e8')}
                                                </View>
                                            )}
                                            {subbaseDesignImages.canvas3 && (
                                                <View style={{ alignItems: 'center', justifyContent: 'flex-end', width: '33.33%', height: '300' }}>
                                                    <Image key={subbaseDesignImages.canvas3.slice(0, 32)} style={pdfSharedStyles.graphicImage} src={subbaseDesignImages.canvas3} />
                                                    <Text style={pdfSharedStyles.graphicDescription}>{t('geogrid_stabilized', 'GEOGRID Stabilized')}</Text>
                                                    {subbaseDesignImages.tableData3 && renderTrafficTable('Optimized Design', subbaseDesignImages.tableData3, '#fff3e0')}
                                                </View>
                                            )}
                                        </View>
                                    </View>
                                </>
                            )}
                        </View>
                    )}
                </>
            )}
        </>
    );
};
